/**
 * Text input with live geocoding suggestions (debounced, see
 * useAddressAutocomplete) — shared by every free-text location field that
 * ultimately gets geocoded: Profile, Event, Action, Group create/settings.
 * `dir="auto"` on both the input and the suggestion rows lets the browser
 * render Hebrew results (and Hebrew typing) right-to-left while still
 * handling Latin-script input left-to-right, instead of forcing one
 * direction — most of what resolves here comes back in Hebrew.
 */
import { useEffect, useRef, useState } from 'react'
import { useAddressAutocomplete, type AddressSuggestion } from '@/hooks'
import type { GeocodedPoint } from '@olimhub/shared'

interface AddressAutocompleteInputProps {
  value: string
  onChange: (value: string) => void
  // Fired when the user picks a suggestion from the dropdown, carrying the
  // resolved point when the pick came from Google Places (undefined for a
  // Nominatim fallback pick — callers fall back to server-side geocoding on
  // save). Also fired with `resolved: undefined` if the user edits the text
  // away from an already-resolved pick, so a stale resolved point can never
  // silently ride along with manually-edited text into a save.
  onSelect?: (value: string, resolved?: GeocodedPoint) => void
  placeholder?: string
  maxLength?: number
  style?: React.CSSProperties
}

export function AddressAutocompleteInput({ value, onChange, onSelect, placeholder, maxLength, style }: AddressAutocompleteInputProps) {
  const [touched, setTouched] = useState(false)
  const suggestions = useAddressAutocomplete(value, touched)
  // Text the last resolved pick is still valid for, and a live mirror of
  // `value` so the async pick below can tell whether the user kept typing
  // before Place Details resolved.
  const resolvedForRef = useRef<string | null>(null)
  const valueRef = useRef(value)
  valueRef.current = value
  // Keyboard-highlighted suggestion — mirrors useMentionAutocomplete's
  // selectedIdx/MentionDropdown convention. Reset to the first suggestion
  // whenever the result set changes (new fetch), so arrow keys always start
  // from a sane position instead of a stale index into the old list.
  const [activeIndex, setActiveIndex] = useState(-1)
  useEffect(() => {
    setActiveIndex(suggestions.results.length > 0 ? 0 : -1)
  }, [suggestions.results])

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!suggestions.isOpen || suggestions.results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, suggestions.results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      void handlePick(suggestions.results[activeIndex]!)
    } else if (e.key === 'Escape') {
      suggestions.close()
    }
  }

  function handleChange(nextValue: string) {
    onChange(nextValue)
    setTouched(true)
    if (resolvedForRef.current !== null && resolvedForRef.current !== nextValue) {
      resolvedForRef.current = null
      onSelect?.(nextValue, undefined)
    }
  }

  async function handlePick(suggestion: AddressSuggestion) {
    suggestions.suppressNextFor(suggestion.label)
    onChange(suggestion.label)
    suggestions.close()
    const resolved = await suggestions.select(suggestion.key)
    // The user kept typing before Place Details resolved — that edit is
    // authoritative now, this pick's coordinates would be stale.
    if (valueRef.current !== suggestion.label) return
    resolvedForRef.current = suggestion.label
    onSelect?.(suggestion.label, resolved ?? undefined)
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        dir="auto"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => suggestions.close(), 150)}
        maxLength={maxLength}
        placeholder={placeholder}
        style={style}
        autoComplete="off"
      />
      {suggestions.isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            // Leaflet's own panes/controls (SearchResultsMap, MapPage) run
            // z-index up to 1000 (.leaflet-top/.leaflet-bottom) with no
            // isolating stacking context around them — any of the app's
            // existing z-index tokens (highest is --z-toast: 100) would
            // still lose to that when this field sits near a map, so this
            // goes well above Leaflet's own max instead.
            zIndex: 1100,
            background: 'var(--surface)',
            border: '1.5px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            marginTop: 4,
            overflow: 'hidden',
          }}
        >
          {suggestions.results.map((suggestion, i) => (
            <button
              key={suggestion.key}
              type="button"
              dir="auto"
              onClick={() => { void handlePick(suggestion) }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'start',
                padding: '8px 12px',
                border: 'none',
                borderBottom: '1px solid var(--border)',
                background: i === activeIndex ? 'var(--brand-soft)' : 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-sm)',
                color: 'var(--text-body)',
                transition: 'background var(--dur-fast)',
              }}
            >
              {suggestion.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
